import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import './TradingChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const TradingChart = ({ data, symbol }) => {
  // Helper function to get DWM color based on normalized value
  const getDWMColor = (normalizedDwmValue) => {
    const ratio = (normalizedDwmValue + 2) / 4; // Convert to 0-1 range
    
    let r, g, b;
    
    if (ratio <= 0.5) {
      // Red to White (0 to 0.5)
      const t = ratio * 2;
      r = 255;
      g = Math.round(255 * t);
      b = Math.round(255 * t);
    } else {
      // White to Blue (0.5 to 1)
      const t = (ratio - 0.5) * 2;
      r = Math.round(255 * (1 - t));
      g = Math.round(255 * (1 - t));
      b = 255;
    }
    
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  };

  // Process data for Chart.js
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        datasets: []
      };
    }

    const timestamps = data.map(item => new Date(item.timestamp));
    
    return {
      labels: timestamps,
      datasets: [
        {
          label: 'Binance US spot '+symbol+'/USDT best bid',
          data: data.map(item => ({ x: new Date(item.timestamp), y: item.best_bid_binance })),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.1,
        },
        {
          label: 'Binance US spot '+symbol+'/USDT best ask',
          data: data.map(item => ({ x: new Date(item.timestamp), y: item.best_ask_binance })),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.1,
        },
        {
          label: 'Bitfinex futures '+symbol+'/USD best bid',
          data: data.map(item => ({ x: new Date(item.timestamp), y: item.best_bid_bitfinex })),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.1,
          borderDash: [5, 5],
          hidden: true,
        },
        {
          label: 'Bitfinex futures '+symbol+'/USD best ask',
          data: data.map(item => ({ x: new Date(item.timestamp), y: item.best_ask_bitfinex })),
          borderColor: 'rgb(255, 206, 86)',
          backgroundColor: 'rgba(255, 206, 86, 0.2)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.1,
          borderDash: [5, 5],
          hidden: true,
        },
        {
          label: 'DWM',
          data: data.map(item => ({ 
            x: new Date(item.timestamp), 
            y: item.normalized_dwm 
          })),
          backgroundColor: data.map(item => getDWMColor(item.normalized_dwm)),
          borderColor: data.map(item => getDWMColor(item.normalized_dwm)),
          pointRadius: 4,
          pointHoverRadius: 6,
          showLine: false,
          yAxisID: 'y1',
        },
      ],
    };
  }, [data, symbol]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        // text: `${symbol}/USD Trading Data with DWM`,
        font: {
          size: 18,
          weight: 'bold',
          family: 'Space Mono',
        },
        padding: {
          top: 10,
          bottom: 30,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'DWM') {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(4)}`;
            }
            return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
            day: 'MMM dd',
          },
        },
        title: {
          display: true,
          text: 'Time',
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Price (USD)',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'DWM Value',
        },
        min: -2.2, // Add padding below -2
        max: 2.2,  // Add padding above +2
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;
    
    const normalizedDwmValues = data.map(item => item.normalized_dwm);
    const mean = normalizedDwmValues.reduce((sum, dwm) => sum + dwm, 0) / normalizedDwmValues.length;
    
    // Calculate variance
    const variance = normalizedDwmValues.reduce((sum, dwm) => sum + Math.pow(dwm - mean, 2), 0) / normalizedDwmValues.length;
    
    // Calculate standard deviation
    const stdDev = Math.sqrt(variance);
    
    // Calculate median
    const sortedValues = [...normalizedDwmValues].sort((a, b) => a - b);
    const median = sortedValues.length % 2 === 0
      ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
      : sortedValues[Math.floor(sortedValues.length / 2)];
    
    // Calculate mode (most frequent value with 3 decimal precision)
    const roundedValues = normalizedDwmValues.map(val => Math.round(val * 1000) / 1000);
    const frequency = {};
    roundedValues.forEach(val => {
      frequency[val] = (frequency[val] || 0) + 1;
    });
    
    const mode = Object.keys(frequency).reduce((a, b) => 
      frequency[a] > frequency[b] ? a : b
    );
    
    return {
      mean: mean,
      median: median,
      mode: parseFloat(mode),
      stdDev: stdDev,
      variance: variance,
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="chart-container card">
        <div className="no-data">
          <p>No data available for the selected timeframe.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container card">
      <div className="chart-wrapper">
        <Line data={chartData} options={options} />
      </div>
      
      {stats && (
        <div className="chart-stats">
          <div className="stats-title">DWM statistics on this timeframe</div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{stats.mean.toFixed(4)}</div>
              <div className="stat-label">Mean</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.median.toFixed(4)}</div>
              <div className="stat-label">Median</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.mode.toFixed(4)}</div>
              <div className="stat-label">Mode</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.stdDev.toFixed(4)}</div>
              <div className="stat-label">Standard Deviation</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.variance.toFixed(4)}</div>
              <div className="stat-label">Variance</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="dwm-legend">
        <div className="stats-title">DWM color scale:</div>
        <div className="dwm-gradient">
          <div className="dwm-gradient-bar"></div>
          <div className="dwm-gradient-labels">
            <span>-2.0 (Red)</span>
            <span>0.0 (White)</span>
            <span>+2.0 (Blue)</span>
          </div>
        </div>
        <div className="dwm-note">
          <small>Note: DWM values are normalized to -2 to +2 range for consistent visualization across different datasets.</small>
        </div>
      </div>
    </div>
  );
};

export default TradingChart; 
